import {
  Sensor,
  useHvacFilter,
  useLogger,
  useSensorAlerts,
  ServiceRequestStatus,
  AlertStatus,
} from 'common';
import { FilterReplacementStatus } from 'common/src/types/hvac/HVACStatus';
import { useCallback, useEffect, useState } from 'react';


export interface HVACFilterAlertData {
  filterReplacementStatus: FilterReplacementStatus;
  alertStatus: FilterReplacementStatus | null; // Current alert state
  serviceStatus: FilterReplacementStatus | null; // Current service/filter order state
  hasActiveService: boolean; // Whether there's any service request (even closed)
  hasActiveFilterOrder: boolean; // Whether there's any filter order (even closed)
  onClearAlert: () => Promise<void>;
  onRequestService: () => Promise<void>;
  onOrderFilter: () => Promise<void>;
}

export const useHVACFilterAlert = (sensor?: Sensor): HVACFilterAlertData => {
  const logger = useLogger('useHVACFilterAlert');
  const { requestService, clearAlert, orderReplacementFilter, getFilterOrderStatus } =
    useHvacFilter();
  const { sensorAlerts } = useSensorAlerts(sensor?.id);

  const [filterReplacementStatus, setFilterReplacementStatus] = useState<FilterReplacementStatus>(
    FilterReplacementStatus.NoFilterChangeNeeded,
  );
  const [alertStatus, setAlertStatus] = useState<FilterReplacementStatus | null>(null);
  const [serviceStatus, setServiceStatus] = useState<FilterReplacementStatus | null>(null);
  const [hasActiveService, setHasActiveService] = useState(false);
  const [hasActiveFilterOrder, setHasActiveFilterOrder] = useState(false);

  const onClearAlert = async () => {
    if (!sensor) {
      logger.error('Missing sensor for clearing alert');
      return;
    }
    try {
      const cleared = await clearAlert(sensor.id, sensor.hubId);
      if (cleared) {
        setFilterReplacementStatus(FilterReplacementStatus.NoFilterChangeNeeded);
      } else {
        logger.error('Failed to clear alert');
      }
    } catch (error: unknown) {
      logger.error('Failed to clear alert:', { error });
    }
  };

  const onRequestService = async () => {
    if (!sensor) {
      logger.error('Missing sensor for requesting service');
      return;
    }
    try {
      const requestedService = await requestService({
        sensorId: sensor.id,
        hubId: sensor.hubId,
        notes: 'HVAC filter needs replacement',
      });

      if (requestedService) {
        setFilterReplacementStatus(FilterReplacementStatus.ServiceRequested);
      } else {
        logger.error('Failed to request service');
      }
    } catch (error: unknown) {
      logger.error('Failed to request service:', { error });
    }
  };

  const onOrderFilter = async () => {
    if (!sensor) {
      logger.error('Missing sensor for ordering filter');
      return;
    }
    try {
      const orderedFilter = await orderReplacementFilter({
        sensorId: sensor.id,
        hubId: sensor.hubId,
        filterType: 'Standard',
        quantity: 1,
        notes: 'Regular replacement filter',
      });

      if (orderedFilter) {
        setFilterReplacementStatus(FilterReplacementStatus.FilterOrdered);
      } else {
        logger.error('Failed to order filter');
      }
    } catch (error: unknown) {
      logger.error('Failed to order filter:', { error });
    }
  };

  const loadFilterStatus = useCallback(
    async () => {
      let currentAlertStatus: FilterReplacementStatus | null = null;
      let currentServiceStatus: FilterReplacementStatus | null = null;
      let hasService = false;
      let hasFilter = false;

      // First: Check for existing service requests or filter orders (only active ones)
      if (sensor?.id && sensor?.hubId) {
        try {
          const filterOrderStatus = await getFilterOrderStatus(sensor.id, sensor.hubId);

          // Check if filter order exists and is NOT closed/canceled
          const hasActiveFilterOrder =
            filterOrderStatus.hasFilterOrder &&
            filterOrderStatus.filterOrder?.status !== ServiceRequestStatus.CLOSED &&
            filterOrderStatus.filterOrder?.status !== ServiceRequestStatus.CANCELED;

          // Check if service request exists and is NOT closed/canceled
          const hasActiveServiceRequest =
            filterOrderStatus.hasServiceRequest &&
            filterOrderStatus.serviceRequest?.status !== ServiceRequestStatus.CLOSED &&
            filterOrderStatus.serviceRequest?.status !== ServiceRequestStatus.CANCELED;

          if (hasActiveFilterOrder) {
            hasFilter = true;
            currentServiceStatus = FilterReplacementStatus.FilterOrdered;
          } else if (hasActiveServiceRequest) {
            hasService = true;
            currentServiceStatus = FilterReplacementStatus.ServiceRequested;
          }
        } catch (error) {
          logger.error('Failed to get filter order status:', { error });
        }
      }

      // Second: Check for unacknowledged alerts
      if (sensorAlerts.length > 0) {
        // Check specifically for HVAC_FILTER alerts that are unacknowledged
        const hvacFilterAlerts = sensorAlerts.filter((alert) => {
          const isHVACFilterAlert = alert.alertType === 'HVAC_FILTER';
          // Support both firmware statuses and actual system statuses
          const isActiveStatus =
            alert.status === AlertStatus.PENDING ||
            alert.status === AlertStatus.ACTIVE ||
            alert.status === 'new' ||
            alert.status === 'ongoing';
          const isUnacknowledged = !alert.acknowledgedAt && !alert.acknowledgedBy;

          return isHVACFilterAlert && isActiveStatus && isUnacknowledged;
        });

        if (hvacFilterAlerts.length > 0) {
          // Check the severity/priority of the alert to determine the specific status
          const alert = hvacFilterAlerts[0]; // Take the first/most recent alert
          let alertStatusValue = FilterReplacementStatus.HVACFilterAlert; // Default

          if (alert.severity === 'critical') {
            alertStatusValue = FilterReplacementStatus.HVACFilterAlertCritical;
          } else if (alert.severity === 'medium') {
            alertStatusValue = FilterReplacementStatus.HVACFilterAlertModerate;
          }

          currentAlertStatus = alertStatusValue;
        }
      }

      // Update all state variables
      setAlertStatus(currentAlertStatus);
      setServiceStatus(currentServiceStatus);
      setHasActiveService(hasService);
      setHasActiveFilterOrder(hasFilter);

      // Determine the primary display status: prioritize service/filter status over alert status
      let finalStatus = FilterReplacementStatus.NoFilterChangeNeeded;
      if (currentServiceStatus) {
        finalStatus = currentServiceStatus;
      } else if (currentAlertStatus) {
        finalStatus = currentAlertStatus;
      }

      setFilterReplacementStatus(finalStatus);
    },
    [sensor?.id, sensor?.hubId, getFilterOrderStatus],
  );

  // Poll filter status every minute, regardless of event or sensor changes
  useEffect(() => {
    if (!sensor?.id || !sensor?.hubId) return;
    loadFilterStatus(); // Run once immediately on mount
    const interval = setInterval(() => {
      loadFilterStatus();
    }, 60000);
    return () => clearInterval(interval);
  }, [loadFilterStatus, sensor?.id, sensor?.hubId]);

  return {
    filterReplacementStatus,
    alertStatus,
    serviceStatus,
    hasActiveService,
    hasActiveFilterOrder,
    onClearAlert,
    onRequestService,
    onOrderFilter,
  };
};
